import { repeatFieldsOf, entryLabelOf } from "../repeatable";
import type { ComponentType, ReactNode } from "react";
import {
  AlignLeftIcon,
  UsersIcon,
  DollarSignIcon,
  CalendarIcon,
  ChevronDownIcon,
  CircleChevronDownIcon,
  CircleDotIcon,
  ClockIcon,
  Grid3x3Icon,
  HeartIcon,
  PaperclipIcon,
  SlidersHorizontalIcon,
  SquareCheckIcon,
  StarIcon,
  TextCursorInputIcon,
  TextIcon,
  ThumbsUpIcon,
  TimerIcon,
  type LucideIcon,
} from "lucide-react";
import { cn } from "@sfc/ui";
import type { Question, QuestionType, RatingIcon } from "../types";
import {
  maxFileSizeMbOf,
  maxFilesOf,
  offersOther,
  optionsOf,
  rowsOf,
} from "../registry";

/**
 * What each question type looks like — its icon in the type picker, and the
 * answer an applicant will be asked for.
 *
 * The builder shows a question the way Google Forms does: the card is the
 * question, so an admin reads the form as an applicant will while editing it.
 * That needs a per-type rendering, which lives here rather than as a
 * `switch (question.type)` in the card, for the same reason the type registry
 * next door exists — the switch that gets forgotten is always the fifth one.
 *
 * Split from `../registry` so that file stays importable from a server or a
 * worker, neither of which can load React.
 *
 * `Preview` is deliberately inert: it is the builder card's *picture* of the
 * question, not a disabled copy of the live control — a disabled control that
 * can never become enabled reads to a screen reader as something the applicant
 * failed to qualify for. The live inputs live next door in `render.tsx` as
 * `Render`, one per type; anyone filling a form in goes through those.
 */
export interface QuestionTypeUI {
  /** Shown beside the type's name in the builder's type picker. */
  icon: LucideIcon;
  /** The answer this type asks for, drawn but not wired to anything. */
  Preview: ComponentType<{ question: Question }>;
}

/** The line a typed answer will be written on. */
function WrittenAnswer({
  placeholder,
  className,
}: {
  placeholder: string;
  className?: string;
}) {
  return (
    <p
      className={cn(
        "max-w-full border-b border-dashed border-border-strong pb-1 text-sm text-muted-foreground",
        className,
      )}
    >
      {placeholder}
    </p>
  );
}

/** A box an applicant picks or types into — dropdown, date, time, file. */
function InputShape({
  children,
  icon: Icon,
  className,
}: {
  children: ReactNode;
  icon: LucideIcon;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "flex w-56 max-w-full items-center justify-between gap-2 rounded-md border border-border-strong px-3 py-1.5 text-sm text-muted-foreground",
        className,
      )}
    >
      <span className="truncate">{children}</span>
      <Icon aria-hidden className="size-4 shrink-0" />
    </div>
  );
}

/**
 * The options a choice question offers, with the control an applicant taps.
 *
 * Drawn rather than rendered with the real radio and checkbox: these are
 * unreachable by definition, and a disabled control that can never become
 * enabled reads to a screen reader as something the applicant failed to
 * qualify for rather than as a picture of the form.
 */
function ChoiceList({
  question,
  shape,
}: {
  question: Question;
  shape: "circle" | "square";
}) {
  const options = optionsOf(question);
  const allowOther = offersOther(question);
  if (options.length === 0 && !allowOther) {
    return (
      <p className="text-sm italic text-muted-foreground">No options yet</p>
    );
  }
  return (
    <ul className="flex flex-col gap-2">
      {options.map((option) => (
        <li key={option.id} className="flex items-center gap-2.5 text-sm">
          <span
            aria-hidden
            className={cn(
              "size-4 shrink-0 border border-border-strong",
              shape === "circle" ? "rounded-full" : "rounded-[4px]",
            )}
          />
          <span className="min-w-0 break-words">{option.label}</span>
        </li>
      ))}
      {allowOther ? (
        <li className="flex items-center gap-2.5 text-sm">
          <span
            aria-hidden
            className={cn(
              "size-4 shrink-0 border border-border-strong",
              shape === "circle" ? "rounded-full" : "rounded-[4px]",
            )}
          />
          <span className="shrink-0">Other:</span>
          <WrittenAnswer placeholder="Other answer" className="w-56 min-w-0" />
        </li>
      ) : null}
    </ul>
  );
}

/** How many of a scale's points are drawn before the middle is elided. */
const DRAWN_SCALE_POINTS = 11;

/**
 * The points to draw for a scale, longer ones elided in the middle.
 *
 * The bounds are stored on the question and nothing validates them yet, so a
 * draft that says 1 to 5000 is a draft, not a reason to draw five thousand
 * circles. Dropping the tail instead would be worse than drawing fewer
 * points: it moves `maxLabel` next to a number that isn't the maximum, and a
 * preview that misstates the question it previews is the one thing it may
 * never do. So the gap goes in the middle and both ends stay true.
 */
export function scalePoints(min: number, max: number): (number | "gap")[] {
  if (max < min) return [];
  const span = max - min + 1;
  if (span <= DRAWN_SCALE_POINTS) {
    return Array.from({ length: span }, (_, index) => min + index);
  }
  return [
    ...Array.from(
      { length: DRAWN_SCALE_POINTS - 1 },
      (_, index) => min + index,
    ),
    "gap",
    max,
  ];
}

function ScalePreview({ question }: { question: Question }) {
  const min = Math.round(question.config?.min ?? 1);
  const max = Math.round(question.config?.max ?? 5);
  return (
    <div className="flex flex-wrap items-end gap-3 text-sm text-muted-foreground">
      {question.config?.minLabel ? (
        <span className="text-xs">{question.config.minLabel}</span>
      ) : null}
      {scalePoints(min, max).map((point) =>
        point === "gap" ? (
          <span key="gap" className="self-center">
            …
          </span>
        ) : (
          <span key={point} className="flex flex-col items-center gap-1">
            <span>{point}</span>
            <span
              aria-hidden
              className="size-4 rounded-full border border-border-strong"
            />
          </span>
        ),
      )}
      {question.config?.maxLabel ? (
        <span className="text-xs">{question.config.maxLabel}</span>
      ) : null}
    </div>
  );
}

export const RATING_ICONS: Record<RatingIcon, LucideIcon> = {
  star: StarIcon,
  heart: HeartIcon,
  thumb: ThumbsUpIcon,
};

function RatingPreview({ question }: { question: Question }) {
  const max = Math.min(Math.max(Math.round(question.config?.max ?? 5), 1), 10);
  const Icon = RATING_ICONS[question.config?.ratingIcon ?? "star"];
  return (
    <div className="flex items-center gap-2 text-muted-foreground">
      {Array.from({ length: max }, (_, index) => (
        <span key={index} className="flex flex-col items-center gap-1 text-sm">
          <span>{index + 1}</span>
          <Icon aria-hidden className="size-4" />
        </span>
      ))}
    </div>
  );
}

/**
 * The grid as the applicant will meet it: rows down, columns across, one
 * pickable control per cell. Drawn, not real controls, for the same reason as
 * `ChoiceList`.
 */
function GridPreview({ question }: { question: Question }) {
  const rows = rowsOf(question);
  const columns = optionsOf(question);
  if (rows.length === 0 || columns.length === 0) {
    return (
      <p className="text-sm italic text-muted-foreground">
        {rows.length === 0 ? "No rows yet" : "No columns yet"}
      </p>
    );
  }
  const shape =
    question.config?.multiSelect === true ? "rounded-[4px]" : "rounded-full";
  return (
    <div className="overflow-x-auto">
      <table className="text-sm">
        <thead>
          <tr>
            <th />
            {columns.map((column) => (
              <th
                key={column.id}
                scope="col"
                className="px-3 pb-2 text-center font-normal text-muted-foreground"
              >
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id}>
              <th scope="row" className="pr-3 text-left font-normal">
                {row.label}
              </th>
              {columns.map((column) => (
                <td key={column.id} className="px-3 py-1.5 text-center">
                  <span
                    aria-hidden
                    className={cn(
                      "inline-block size-4 border border-border-strong",
                      shape,
                    )}
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** "One file, up to 25 MB" / "Up to 3 files, each up to 25 MB". */
export function fileUploadSummary(question: Question): string {
  const count = maxFilesOf(question);
  const size = maxFileSizeMbOf(question);
  return count === 1
    ? `One file, up to ${size} MB`
    : `Up to ${count} files, each up to ${size} MB`;
}

export const QUESTION_TYPE_UI: Record<QuestionType, QuestionTypeUI> = {
  short_answer: {
    icon: TextCursorInputIcon,
    Preview: () => (
      <WrittenAnswer placeholder="Short answer text" className="w-64" />
    ),
  },
  money: {
    icon: DollarSignIcon,
    Preview: () => <WrittenAnswer placeholder="USD 0.00" className="w-64" />,
  },
  paragraph: {
    icon: AlignLeftIcon,
    Preview: () => (
      <WrittenAnswer placeholder="Long answer text" className="w-full" />
    ),
  },
  multiple_choice: {
    icon: CircleDotIcon,
    Preview: ({ question }) => (
      <ChoiceList question={question} shape="circle" />
    ),
  },
  checkboxes: {
    icon: SquareCheckIcon,
    Preview: ({ question }) => (
      <ChoiceList question={question} shape="square" />
    ),
  },
  dropdown: {
    icon: CircleChevronDownIcon,
    Preview: ({ question }) => (
      <InputShape icon={ChevronDownIcon}>
        {optionsOf(question).length > 0 ? "Choose" : "No options yet"}
      </InputShape>
    ),
  },
  date: {
    icon: CalendarIcon,
    Preview: ({ question }) => {
      const parts = [
        question.config?.monthDayOnly === true
          ? "Month, day"
          : "Day, month, year",
        ...(question.config?.includeTime === true ? ["time"] : []),
      ];
      return <InputShape icon={CalendarIcon}>{parts.join(" and ")}</InputShape>;
    },
  },
  time: {
    icon: ClockIcon,
    Preview: ({ question }) =>
      question.config?.duration === true ? (
        <InputShape icon={TimerIcon}>Hours, minutes</InputShape>
      ) : (
        <InputShape icon={ClockIcon}>Time</InputShape>
      ),
  },
  file_upload: {
    icon: PaperclipIcon,
    Preview: ({ question }) => (
      <InputShape icon={PaperclipIcon} className="border-dashed">
        {fileUploadSummary(question)}
      </InputShape>
    ),
  },
  linear_scale: {
    icon: SlidersHorizontalIcon,
    Preview: ScalePreview,
  },
  rating: {
    icon: StarIcon,
    Preview: RatingPreview,
  },
  grid: {
    icon: Grid3x3Icon,
    Preview: GridPreview,
  },
  repeatable_group: {
    icon: UsersIcon,
    Preview: ({ question }) => (
      <div className="flex flex-col gap-2">
        {repeatFieldsOf(question).map((field) => (
          <WrittenAnswer
            key={field.id}
            placeholder={field.label || "Untitled field"}
          />
        ))}
        <p className="body-sm text-muted-foreground">
          Add {entryLabelOf(question).toLowerCase()}
        </p>
      </div>
    ),
  },
  text_block: {
    icon: TextIcon,
    // The card already shows the block's title and text — its label and help
    // — and there is nothing to answer, so the preview has nothing to add.
    Preview: () => null,
  },
};

export function uiFor(type: QuestionType): QuestionTypeUI {
  return QUESTION_TYPE_UI[type];
}
